/**
 * Outcome Drivers Page
 *
 * Workspace-level list of saved Outcome Driver artifacts.
 * Satisfies §2.4 proof-of-life: drivers persist across refresh,
 * can be viewed and expanded after creation.
 *
 * Route: /prioritization/outcome-drivers
 */

import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Target,
  Plus,
  ChevronDown,
  ChevronUp,
  Loader2,
  AlertCircle,
  Trash2,
  Layers,
  Calendar,
  LayoutList,
  Table2,
  TreeDeciduous,
} from 'lucide-react';
import { PageHeader } from '../../components/prioritization/PageHeader';
import { StrategyConnectivityHelpButton } from './strategy/StrategyConnectivityDocModal';
import { EntitySyncPanel } from '../../components/ui/EntitySyncPanel';
import {
  useOutcomeDriverStore,
  type OutcomeDriver,
} from '../../stores/prioritization/outcomeDriverStore';
import { useStrategyStore } from '../../stores/prioritization/strategyStore';
import { useTenantStore } from '../../stores/prioritization/tenantStore';
import { getItemCountState } from '../../components/collection/types';

interface StrategyOption { id: string; name: string; }

// ── Driver card ───────────────────────────────────────────────

function DriverCard({ driver, onDelete, strategies, onUpdateStrategy }: {
  driver: OutcomeDriver;
  onDelete: (id: string) => void;
  strategies: StrategyOption[];
  onUpdateStrategy: (driverId: string, strategyId: string | null) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const FIELDS: { label: string; key: keyof OutcomeDriver }[] = [
    { label: 'Context of Purpose', key: 'contextOfPurpose' },
    { label: 'Current State', key: 'currentState' },
    { label: 'Problem / Opportunity', key: 'problemOpportunity' },
    { label: 'Ramifications', key: 'ramifications' },
    { label: 'Narrative', key: 'narrative' },
  ];

  const linkedStrategy = strategies.find((s) => s.id === driver.strategyId);

  async function handleDelete() {
    if (!confirm(`Delete "${driver.title}"?`)) return;
    setDeleting(true);
    await onDelete(driver.id);
  }

  return (
    <div className={`border  bg-[var(--bg-raised)] dark:bg-[var(--surface)] transition-all ${
      expanded ? 'border-[var(--slate)] dark:border-[var(--slate)]' : 'border-[var(--rule)]'
    }`}>
      {/* Header row */}
      <div className="flex items-start gap-3 px-5 py-4">
        <button
          onClick={() => setExpanded((v) => !v)}
          className="flex-1 text-left min-w-0"
        >
          <div className="flex items-center gap-2">
            <Target className="h-4 w-4 text-[var(--text-ter)] flex-shrink-0" />
            <h3 className="text-sm font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)] truncate">
              {driver.title || 'Untitled driver'}
            </h3>
          </div>
          {driver.narrative && !expanded && (
            <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] mt-1 line-clamp-2 ml-6">
              {driver.narrative}
            </p>
          )}
        </button>

        <div className="flex items-center gap-1.5 flex-shrink-0">
          {/* ↑ Upstream: Strategy this driver names */}
          {linkedStrategy ? (
            <Link to={`/prioritization/strategy/${linkedStrategy.id}`}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 border border-primary-100 dark:border-primary-800 hover:bg-primary-100 transition-colors"
              title="View linked Strategy"
            >
              <TreeDeciduous className="h-2.5 w-2.5" />
              {linkedStrategy.name}
            </Link>
          ) : null}
          {driver.cycleId && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-[var(--slate-dim)] dark:bg-[var(--slate)]/30 text-[var(--text-sec)] border border-[var(--slate)] dark:border-[var(--slate)]">
              <Calendar className="h-2.5 w-2.5" />
              cycle
            </span>
          )}
          <button
            onClick={() => setExpanded((v) => !v)}
            className="p-1 rounded hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-tint)] text-[var(--text-ter)]"
            title={expanded ? 'Collapse' : 'Expand'}
          >
            {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
          <button
            onClick={handleDelete}
            disabled={deleting}
            className="p-1 rounded hover:bg-[var(--coral-dim)] dark:hover:bg-[var(--coral)]/30 text-[var(--text-ter)] hover:text-[var(--coral)] transition-colors disabled:opacity-50"
            title="Delete driver"
          >
            {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
          </button>
        </div>
      </div>

      {/* Expanded fields */}
      {expanded && (
        <div className="px-5 pb-5 space-y-4 border-t border-[var(--rule)] pt-4">
          {FIELDS.map(({ label, key }) => {
            const val = driver[key] as string;
            if (!val) return null;
            return (
              <div key={key}>
                <p className="text-[10px] font-semibold text-[var(--text-ter)] dark:text-[var(--text-ter)] uppercase tracking-wide mb-1">
                  {label}
                </p>
                <p className="text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)] leading-relaxed whitespace-pre-wrap">
                  {val}
                </p>
              </div>
            );
          })}
          {/* ↑ Upstream: Strategy link */}
          {strategies.length > 0 && (
            <div>
              <p className="text-[10px] font-semibold text-[var(--text-ter)] uppercase tracking-wide mb-1">
                Names opportunity space for Strategy
              </p>
              <div className="relative">
                <select
                  value={driver.strategyId ?? ''}
                  onChange={(e) => onUpdateStrategy(driver.id, e.target.value || null)}
                  className="w-full px-3 py-1.5 text-sm border border-[var(--rule)] bg-[var(--bg-raised)] text-[var(--text-pri)] appearance-none pr-8 focus:outline-none focus:border-primary-500"
                >
                  <option value="">— Not linked to a strategy —</option>
                  {strategies.map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
                <span className="pointer-events-none absolute inset-y-0 right-2 flex items-center text-[var(--text-ter)]">
                  <svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
                  </svg>
                </span>
              </div>
            </div>
          )}
          <p className="text-[10px] text-[var(--text-ter)] mt-2">
            Created {new Date(driver.createdAt).toLocaleDateString()}
          </p>
        </div>
      )}
    </div>
  );
}

// ── Driver table row ──────────────────────────────────────────

function DriverTableRow({ driver, onDelete, strategies }: {
  driver: OutcomeDriver;
  onDelete: (id: string) => void;
  strategies: StrategyOption[];
}) {
  const [deleting, setDeleting] = useState(false);
  const linkedStrategy = strategies.find((s) => s.id === driver.strategyId);
  async function handleDelete() {
    if (!confirm(`Delete "${driver.title}"?`)) return;
    setDeleting(true);
    await onDelete(driver.id);
  }
  return (
    <tr className="border-b border-[var(--rule)] hover:bg-[var(--surface)] dark:hover:bg-[var(--bg-raised)]/40 transition-colors">
      <td className="px-4 py-2.5">
        <div className="flex items-center gap-2">
          <Target className="h-3.5 w-3.5 text-[var(--text-ter)] flex-shrink-0" />
          <span className="text-sm font-medium text-[var(--text-pri)] truncate max-w-xs">
            {driver.title || 'Untitled driver'}
          </span>
        </div>
        {driver.narrative && (
          <p className="text-xs text-[var(--text-ter)] ml-5 mt-0.5 truncate max-w-sm">{driver.narrative}</p>
        )}
      </td>
      {/* ↑ Upstream: Strategy */}
      <td className="px-3 py-2.5 w-40">
        {linkedStrategy ? (
          <Link to={`/prioritization/strategy/${linkedStrategy.id}`}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 border border-primary-100 dark:border-primary-800 hover:bg-primary-100 transition-colors max-w-[9rem] truncate"
          >
            <TreeDeciduous className="h-2.5 w-2.5 flex-shrink-0" />
            <span className="truncate">{linkedStrategy.name}</span>
          </Link>
        ) : driver.cycleId ? (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-[var(--slate-dim)] dark:bg-[var(--slate)]/30 text-[var(--text-sec)] border border-[var(--slate)] dark:border-[var(--slate)]">
            <Calendar className="h-2.5 w-2.5" /> cycle
          </span>
        ) : null}
      </td>
      <td className="px-3 py-2.5 w-28 text-xs text-[var(--text-ter)]">
        {new Date(driver.createdAt).toLocaleDateString()}
      </td>
      <td className="px-3 py-2.5 w-10 text-right">
        <button onClick={handleDelete} disabled={deleting}
          className="p-1 text-[var(--text-ter)] hover:text-[var(--coral)] dark:hover:text-[var(--coral)] transition-colors disabled:opacity-50">
          {deleting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
        </button>
      </td>
    </tr>
  );
}

// ── Main page ─────────────────────────────────────────────────

export default function OutcomeDriversPage() {
  const navigate = useNavigate();
  const currentWorkspaceId = useTenantStore((s) => s.currentWorkspaceId);
  const { drivers, sets, syncState, fetchFromSupabase, deleteDriver, updateDriver, createDriver } = useOutcomeDriverStore();
  const strategyMap     = useStrategyStore((s) => s.strategies);
  const fetchStrategies = useStrategyStore((s) => s.fetchFromSupabase);
  const strategies      = useMemo<StrategyOption[]>(
    () => Object.values(strategyMap).map((s) => ({ id: s.id, name: s.name })),
    [strategyMap],
  );
  const [viewMode, setViewMode] = useState<'cards' | 'table' | null>(null);

  useEffect(() => {
    fetchFromSupabase();
    fetchStrategies();
  }, [fetchFromSupabase, fetchStrategies, currentWorkspaceId]);

  const handleUpdateStrategy = (driverId: string, strategyId: string | null) => {
    updateDriver(driverId, { strategyId });
  };

  const isLoading = syncState.isLoading;
  const error = syncState.syncError;
  const countState = getItemCountState(drivers.length);
  const effectiveView = viewMode ?? (countState === 'many' ? 'table' : 'cards');

  return (
    <div className="space-y-6">
      <PageHeader
        icon={<Target className="h-4 w-4 text-[var(--text-ter)]" />}
        iconBg="bg-[var(--slate-dim)] dark:bg-[var(--slate)]/30"
        title="Outcome Drivers"
        subtitle="Structured artifacts that define why work matters — context, state, opportunity, and ramifications"
        actions={
          <div className="flex items-center gap-2">
            <StrategyConnectivityHelpButton context="outcome_drivers" />
            {drivers.length > 0 && (
              <div className="flex items-center border border-[var(--rule)] overflow-hidden">
                <button onClick={() => setViewMode('cards')} title="Card view"
                  className={`p-1.5 transition-colors ${effectiveView === 'cards' ? 'bg-[var(--hover-tint)] text-[var(--text-sec)]' : 'text-[var(--text-ter)] hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)]'}`}>
                  <LayoutList className="h-3.5 w-3.5" />
                </button>
                <button onClick={() => setViewMode('table')} title="Table view"
                  className={`p-1.5 transition-colors ${effectiveView === 'table' ? 'bg-[var(--hover-tint)] text-[var(--text-sec)]' : 'text-[var(--text-ter)] hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)]'}`}>
                  <Table2 className="h-3.5 w-3.5" />
                </button>
              </div>
            )}
            <button
              onClick={() => navigate('/prioritization/outcome-drivers/new')}
              className="flex items-center gap-2 px-3 py-2 bg-[var(--slate)] hover:bg-[var(--slate)] text-white text-sm font-medium transition-colors"
            >
              <Plus className="h-4 w-4" />
              Generate Driver
            </button>
          </div>
        }
      />

      <EntitySyncPanel
        entityType="outcome_drivers"
        data={drivers.map((d) => ({
          title: d.title ?? '',
          context_of_purpose: d.contextOfPurpose ?? '',
          current_state: d.currentState ?? '',
          problem_opportunity: d.problemOpportunity ?? '',
          ramifications: d.ramifications ?? '',
          narrative: d.narrative ?? '',
        }))}
        filename="outcome-drivers"
        onImport={async (rows) => {
          // Return created identities so the panel registers external identity
          // + a change event for connector-sourced rows (set 5B convergence).
          const refs: { externalId: string; internalId: string; externalUrl?: string; label?: string }[] = [];
          for (const row of rows) {
            const { driver } = await createDriver({
              title: String(row.title ?? ''),
              contextOfPurpose: row.context_of_purpose ? String(row.context_of_purpose) : undefined,
              currentState: row.current_state ? String(row.current_state) : undefined,
              problemOpportunity: row.problem_opportunity ? String(row.problem_opportunity) : undefined,
              ramifications: row.ramifications ? String(row.ramifications) : undefined,
              narrative: row.narrative ? String(row.narrative) : undefined,
            });
            if (driver?.id && row.external_id) {
              refs.push({
                externalId: String(row.external_id),
                internalId: driver.id,
                ...(row.external_url ? { externalUrl: String(row.external_url) } : {}),
                label: String(row.title ?? ''),
              });
            }
          }
          return refs;
        }}
      />

      {/* Loading */}
      {isLoading && (
        <div className="flex items-center justify-center py-16 text-[var(--text-ter)]">
          <Loader2 className="h-6 w-6 animate-spin mr-2" />
          Loading drivers…
        </div>
      )}

      {/* Error */}
      {!isLoading && error && (
        <div className="flex items-center gap-2 p-4 bg-[var(--coral-dim)] dark:bg-[var(--coral)]/20 border border-[var(--coral)] dark:border-[var(--coral)] text-[var(--coral)] dark:text-[var(--coral)] text-sm">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          {error}
          <button onClick={fetchFromSupabase} className="ml-auto underline hover:no-underline">
            Retry
          </button>
        </div>
      )}

      {/* Empty state */}
      {!isLoading && !error && drivers.length === 0 && (
        <div className="text-center py-16 border border-dashed border-[var(--rule)]">
          <Target className="h-10 w-10 text-[var(--text-ter)] mx-auto mb-3" />
          <h3 className="text-base font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] mb-1">
            No Outcome Drivers yet
          </h3>
          <p className="text-sm text-[var(--text-ter)] dark:text-[var(--text-ter)] mb-4 max-w-sm mx-auto">
            Outcome Drivers define the "why" behind your work — context, current state, problem/opportunity,
            and ramifications. Generate one to get started.
          </p>
          <button
            onClick={() => navigate('/prioritization/outcome-drivers/new')}
            className="inline-flex items-center gap-2 px-4 py-2 bg-[var(--slate)] hover:bg-[var(--slate)] text-white text-sm font-medium transition-colors"
          >
            <Plus className="h-4 w-4" />
            Generate your first Outcome Driver
          </button>
        </div>
      )}

      {/* Sets */}
      {!isLoading && !error && sets.length > 0 && (
        <div>
          <h2 className="flex items-center gap-2 text-xs font-semibold text-[var(--text-ter)] dark:text-[var(--text-ter)] uppercase tracking-wide mb-3">
            <Layers className="h-3.5 w-3.5" />
            Driver Sets ({sets.length})
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
            {sets.map((s) => (
              <div
                key={s.id}
                className="p-4 border border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--surface)]"
              >
                <div className="flex items-center gap-2 mb-1">
                  <Layers className="h-3.5 w-3.5 text-[var(--text-ter)] flex-shrink-0" />
                  <span className="text-sm font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">
                    {s.title}
                  </span>
                </div>
                <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">
                  Created {new Date(s.createdAt).toLocaleDateString()}
                  {s.cycleId && ' · linked to cycle'}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Driver list */}
      {!isLoading && !error && drivers.length > 0 && (
        <div>
          <h2 className="flex items-center gap-2 text-xs font-semibold text-[var(--text-ter)] dark:text-[var(--text-ter)] uppercase tracking-wide mb-3">
            <Target className="h-3.5 w-3.5" />
            All Drivers ({drivers.length})
          </h2>

          {effectiveView === 'cards' && (
            <div className="space-y-3">
              {drivers.map((d) => (
                <DriverCard key={d.id} driver={d} onDelete={deleteDriver} strategies={strategies} onUpdateStrategy={handleUpdateStrategy} />
              ))}
            </div>
          )}

          {effectiveView === 'table' && (
            <div className="border border-[var(--rule)] overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="bg-[var(--surface)] dark:bg-[var(--bg-raised)]/60 border-b border-[var(--rule)]">
                    <th className="px-4 py-2 text-left text-[10px] font-semibold text-[var(--text-ter)] uppercase tracking-wide">Driver</th>
                    <th className="px-3 py-2 w-40 text-left text-[10px] font-semibold text-[var(--text-ter)] uppercase tracking-wide">Strategy</th>
                    <th className="px-3 py-2 w-28 text-left text-[10px] font-semibold text-[var(--text-ter)] uppercase tracking-wide">Created</th>
                    <th className="w-10" />
                  </tr>
                </thead>
                <tbody>
                  {drivers.map((d) => (
                    <DriverTableRow key={d.id} driver={d} onDelete={deleteDriver} strategies={strategies} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
